import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Users, Search } from "lucide-react";

/** AssignMoreTestersDialog — clones the current task to N approved freelancers. */
export default function AssignMoreTestersDialog({ open, onOpenChange, taskId, existingAssignees = [], onCloned }) {
  const [testers, setTesters] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/users", { params: { role: "freelancer" } });
        const approved = (data || []).filter(u => u.status === "approved");
        setTesters(approved);
        setSelected([]);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load testers");
      }
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const already = new Set(existingAssignees || []);
    const term = q.trim().toLowerCase();
    return testers.filter(u =>
      !already.has(u.id) && (!term || (u.name || "").toLowerCase().includes(term) || (u.email || "").toLowerCase().includes(term))
    );
  }, [testers, q, existingAssignees]);

  const submit = async () => {
    if (selected.length === 0) { toast.error("Pick at least one tester"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${taskId}/clone-to-testers`, { tester_ids: selected });
      const n = data.created_task_ids?.length || 0;
      toast.success(`Assigned ${n} tester${n === 1 ? "" : "s"} to this task`);
      onOpenChange(false);
      onCloned?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="assign-more-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5"/> Assign more testers</DialogTitle>
          <DialogDescription>
            Each selected tester gets a fresh copy of this task with status <b>Assigned</b>. Submissions will roll up into the same task-group view.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400"/>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or email" className="pl-8" data-testid="assign-more-search"/>
          </div>
          <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md divide-y">
            {filtered.length === 0 && <div className="p-3 text-xs text-slate-500">No matching approved testers.</div>}
            {filtered.map(u => (
              <label key={u.id} className="flex items-center gap-2 p-2 cursor-pointer hover:bg-slate-50" data-testid={`assign-more-row-${u.id}`}>
                <Checkbox checked={selected.includes(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`assign-more-check-${u.id}`}/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{u.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{u.email}{u.tier ? ` · ${u.tier}` : ""}</div>
                </div>
              </label>
            ))}
          </div>
          <div className="text-[11px] text-slate-500">{selected.length} tester{selected.length === 1 ? "" : "s"} selected</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || selected.length === 0} className="bg-slate-900 hover:bg-slate-800" data-testid="assign-more-confirm">
            {busy ? "Assigning…" : `Assign ${selected.length || ""}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
